import { Ticket, TicketAttachment, TicketSummary } from '../types/ticket';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

function getToken(): string {
  return localStorage.getItem('token') || '';
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_BASE}${url}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('未登录');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
}

export interface CreateTicketPayload {
  category: string;
  title: string;
  projectId?: string;
  relatedType?: string;
  relatedId?: string;
  content: string;
  attachments?: TicketAttachment[];
}

export interface PaginatedTickets {
  data: Ticket[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface AdminListTicketsParams {
  // `status` may be a single TicketStatus or a comma-separated list
  // (e.g. "open,in_progress"), matching the backend's multi-status filter.
  status?: string;
  category?: string;
  projectId?: string;
  userId?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export const createTicket = (body: CreateTicketPayload): Promise<Ticket> =>
  request('/tickets', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const listMyTickets = (
  // `status` may be a single TicketStatus or a comma-separated list
  // (e.g. "open,in_progress"), matching the backend's multi-status filter.
  status?: string,
  page: number = 1,
  limit: number = 20,
): Promise<PaginatedTickets> => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  params.set('page', String(page));
  params.set('limit', String(limit));
  return request(`/tickets?${params.toString()}`);
};

export const getTicketsSummary = (): Promise<TicketSummary> =>
  request('/tickets/summary');

export const getTicket = (id: string): Promise<Ticket> =>
  request(`/tickets/${encodeURIComponent(id)}`);

export const addTicketMessage = (
  id: string,
  content: string,
  attachments?: TicketAttachment[],
): Promise<Ticket> =>
  request(`/tickets/${encodeURIComponent(id)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ content, attachments }),
  });

export const resolveTicket = (id: string, content?: string): Promise<Ticket> =>
  request(`/tickets/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });

export const reopenTicket = (id: string): Promise<Ticket> =>
  request(`/tickets/${encodeURIComponent(id)}/reopen`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const closeTicket = (id: string): Promise<Ticket> =>
  request(`/tickets/${encodeURIComponent(id)}/close`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const uploadTicketAttachment = async (file: File): Promise<TicketAttachment> => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const response = await fetch(`${API_BASE}/ticket-attachments`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('未登录');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: '上传失败' }));
    throw new Error(error.error || '上传失败');
  }
  return response.json();
};

export const adminListTickets = (params: AdminListTicketsParams = {}): Promise<PaginatedTickets> => {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.category) search.set('category', params.category);
  if (params.projectId) search.set('projectId', params.projectId);
  if (params.userId) search.set('userId', params.userId);
  if (params.q) search.set('q', params.q);
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();
  return request(`/admin/tickets${qs ? `?${qs}` : ''}`);
};
